/**
 * 조직 목록 페이지
 * HomeContent와 완전히 동일한 디자인
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { SearchBar } from '@/components/SearchBar';
import { OrganizationCard } from '@/components/cards/OrganizationCard';
import { useOrganizations } from '@/hooks/useOrganizations';

export function OrganizationsPage() {
  const navigate = useNavigate();
  const { organizations, loading, error } = useOrganizations();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return organizations;
    const query = searchQuery.toLowerCase();
    return organizations.filter(
      (org) =>
        org.name?.toLowerCase().includes(query) ||
        org.description?.toLowerCase().includes(query)
    );
  }, [organizations, searchQuery]);

  const handleOrganizationClick = (slug: string) => {
    navigate(`/org/${slug}`);
  };

  return (
    <div className="flex-1 pb-[calc(5rem+2vw)] p-[0px] m-[0px]">
      <SearchBar
        placeholder="Buscar organizaciones..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      <div className="p-[min(5vw,1.25rem)]">
        <div className="flex items-center justify-between m-[0px]">
          <h2 className="text-[16px] font-medium text-foreground">
            Elige tu organización favorita
          </h2>
        </div>
        <p className="text-[14px] text-muted-foreground mb-[min(3vw,1rem)]">
          {filteredOrganizations.length} organizaciones
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Cargando...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">{error.message}</p>
          </div>
        ) : filteredOrganizations.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No se encontraron organizaciones'
                : 'No hay organizaciones disponibles'}
            </p>
          </div>
        ) : (
          <div className="space-y-[min(2vw,0.5rem)]">
            {filteredOrganizations.map((org, index) => (
              <div
                key={org.id}
                className="transform transition-all duration-500 ease-out opacity-0 translate-y-4 animate-fadeInUp"
                style={{
                  animationDelay: `${index * 100}ms`,
                  animationFillMode: 'forwards',
                }}
              >
                <div className="hover:scale-[1.02] transition-transform duration-200 hover:shadow-lg rounded-2xl">
                  <OrganizationCard
                    id={org.id}
                    slug={org.slug}
                    name={org.name}
                    description={org.description}
                    logoUrl={org.logo_url}
                    storesCount={org.stores_count}
                    isActive={org.is_active}
                    onClick={handleOrganizationClick}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
